/** @jsxImportSource @emotion/react */

import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { DivProps } from '../../../../typings';
import { Portal } from 'ud-ui/components/portal';
import { X } from 'lucide-react';
import { css } from '@emotion/react';
import { theme } from 'styles/theme/theme';
import styled from '@emotion/styled';

export default function useCustomModal(props?: IProps) {
  const { onClose, background } = props ?? {};
  const [state, setState] = useState<ModalState>({
    isOpen: false,
    modalStyle: {},
    iconProps: {},
    containerProps: {},
    overlayProps: {},
    childrenContainerProps: {},
  });

  const isOpen = useMemo(() => {
    return state.isOpen;
  }, [state.isOpen]);

  const close = useCallback(() => {
    setState({ isOpen: false, modalStyle: {} });
    onClose?.();
  }, [onClose]);

  const [closeIcon, setCloseIcon] = useState<ReactNode>();

  const open = useCallback((styles: ModalStyles, closeIcon?: React.ReactNode) => {
    setState({ isOpen: true, ...styles });
    setCloseIcon(closeIcon);
  }, []);

  const backgroundColor = useMemo(() => {
    switch (background) {
      case 'white':
        return '#FFFFFF';
      case 'gray':
        return '#F2F2F2';
      case 'black':
        return '#000000';
      default:
        return '#FFFFFF';
    }
  }, [background]);

  const defaultOpen = useCallback(
    (additionalStyle?: Partial<ModalStyles>, closeIcon?: React.ReactNode) => {
      open(
        {
          ...additionalStyle,
          modalStyle: {
            padding: 0,
            zIndex: 9000,
            position: 'fixed',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100vw',
            height: '100vh',
            backgroundColor,
            top: 0,
            left: 0,
            borderRadius: 0,
            borderColor: 'transparent',
            overflow: 'clip',
            background: '#00000085',
            ...additionalStyle?.modalStyle,
          },
        },
        closeIcon,
      );
    },
    [backgroundColor, open],
  );

  useEffect(() => {
    const onPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', onPress);
    return () => {
      document.removeEventListener('keydown', onPress);
    };
  }, [close]);

  const Modal = useCallback(
    (props: DivProps) => {
      const { children, ...restProps } = props;
      return (
        <Portal wrapperId={Math.random().toString()}>
          {state.isOpen ? (
            <ModalContainer {...restProps} style={{ ...state.modalStyle, ...props.style }}>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  width: '100vw',
                  height: '100vh',
                }}
                onClick={close}
                {...state.overlayProps}
              />
              <div {...state.containerProps} css={css(modalStyle, state.containerProps?.css)}>
                <div className="icon-wrapper" css={iconModalCss} {...state.iconProps} onClick={close}>
                  {closeIcon ?? <X size={20} className="modal-close-icon cursor-pointer" color={theme.colors.Gray} />}
                </div>
                <div
                  {...state.childrenContainerProps}
                  style={{
                    ...state.childrenContainerProps?.style,
                    height: '100%',
                  }}
                >
                  {children}
                </div>
              </div>
            </ModalContainer>
          ) : (
            <></>
          )}
        </Portal>
      );
    },
    [
      close,
      closeIcon,
      state.childrenContainerProps,
      state.containerProps,
      state.iconProps,
      state.isOpen,
      state.modalStyle,
      state.overlayProps,
    ],
  );

  return { Modal, close, open, isOpen, defaultOpen };
}

interface IProps {
  onClose?: () => void;
  background?: 'white' | 'gray' | 'black';
}

interface ModalState extends ModalStyles {
  isOpen: boolean;
}

export interface ModalStyles {
  modalStyle: Partial<React.CSSProperties>;
  containerProps?: DivProps;
  overlayProps?: DivProps;
  iconProps?: DivProps;
  childrenContainerProps?: DivProps;
}

const modalStyle = css`
  z-index: 1001;

  background-color: #fff;
  border: 1px solid #ededed;
  box-shadow: 4px 4px 4px 0 #00000040;
  width: 500px;
  min-height: 200px;
  border-radius: 8px;

  font-family:
    SF Pro Text,
    sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;

  display: flex;
  flex-direction: column;

  padding: 34px 24px;
  position: relative;
`;

const iconModalCss = css`
  position: absolute !important;
  top: 0;
  right: 0;
  padding: 22px;
  cursor: pointer;
`;

const ModalContainer = styled.div`
  position: absolute;
  background-color: white;
  box-shadow: 0 4px 4px #00000040;
  padding-block: 8px;
  z-index: 100;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
`;
